import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Space,
  Tag,
  Tooltip,
  message,
  Empty,
  Grid,
  Collapse
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LoginOutlined,
  ExportOutlined,
  SwapOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import EntityCardList from './EntityCardList';

const { Option } = Select;
const { useBreakpoint } = Grid;
const DEFAULT_ZONE_NAME = 'Event Venue';
const { TextArea } = Input;

// One line of the guide: a fixed-width badge column so the descriptions line up, with room to breathe.
const GuideRow = ({ tag, children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
    <div style={{ minWidth: 92, flexShrink: 0 }}>{tag}</div>
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
  </div>
);

/**
 * Gate Management Component
 * Allows configuring multiple gates/entrances/exits for an event.
 * All main gates share one event-wide zone, while each session gate gets
 * its own child zone under that event zone.
 */
const GateManagement = ({ eventId, gates = [], onChange }) => {
  const { t, i18n } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const isRtl = i18n.dir ? i18n.dir() === 'rtl' : i18n.language?.startsWith('ar');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingGate, setEditingGate] = useState(null);
  const [gateLevel, setGateLevel] = useState('main');
  const [form] = Form.useForm();

  const getEventMainZoneId = () =>
    `event_${String(eventId || 'unknown').replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}`;

  const getGateScope = (gate) =>
    gate?.gateScope || (gate?.parentZoneId ? 'session' : 'main');

  const getDefaultMainZoneName = () => {
    const existingMainZoneName = gates.find(g => getGateScope(g) === 'main')?.zoneName;
    return existingMainZoneName || DEFAULT_ZONE_NAME;
  };

  // The default zone name is persisted in English; show it translated.
  const displayZoneName = (name) =>
    !name || name === DEFAULT_ZONE_NAME ? t('eventForm.gates.defaultZoneName') : name;

  const buildSessionZoneId = (gateId) =>
    `session_${String(gateId).replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}`;

  useEffect(() => {
    if (!gates || gates.length === 0) {
      return;
    }

    const mainZoneId = getEventMainZoneId();
    const mainZoneName = getDefaultMainZoneName();

    const normalizedGates = gates.map(gate => {
      const gateScope = getGateScope(gate);

      if (gateScope === 'session') {
        return {
          ...gate,
          gateScope: 'session',
          gateType: 'both',
          zoneId: gate.zoneId || buildSessionZoneId(gate.gateId),
          zoneName: gate.zoneName || gate.gateName || t('eventForm.gates.unnamedSession'),
          parentZoneId: mainZoneId
        };
      }

      return {
        ...gate,
        gateScope: 'main',
        zoneId: mainZoneId,
        zoneName: mainZoneName,
        parentZoneId: null
      };
    });

    if (JSON.stringify(normalizedGates) !== JSON.stringify(gates)) {
      onChange(normalizedGates);
    }
  }, [eventId, gates, onChange]);

  const gateTypeIcons = {
    entry: <LoginOutlined style={{ color: '#52c41a' }} />,
    exit: <ExportOutlined style={{ color: '#fa8c16' }} />,
    both: <SwapOutlined style={{ color: '#1890ff' }} />
  };

  const gateTypeColors = {
    entry: 'green',
    exit: 'orange',
    both: 'blue'
  };

  const columns = [
    {
      title: t('eventForm.gates.columns.gate'),
      dataIndex: 'gateName',
      key: 'gateName',
      width: 150,
      render: (text, record) => (
        <Space>
          {gateTypeIcons[record.gateType]}
          <strong>{text}</strong>
          {!record.isActive && <Tag color="red">{t('eventForm.gates.inactive')}</Tag>}
        </Space>
      )
    },
    {
      title: t('eventForm.gates.columns.type'),
      dataIndex: 'gateType',
      key: 'gateType',
      width: 80,
      render: (type) => (
        <Tag color={gateTypeColors[type]}>
          {t(`eventForm.gates.types.${type}`, type)}
        </Tag>
      )
    },
    {
      title: t('eventForm.gates.columns.zone'),
      dataIndex: 'zoneName',
      key: 'zoneName',
      width: 170,
      render: (zoneName, record) => {
        if (getGateScope(record) === 'session') {
          return (
            <Space direction="vertical" size={0}>
              <span style={{ fontWeight: 500 }}>{zoneName}</span>
              <span style={{ fontSize: '11px', color: '#999' }}>{t('eventForm.gates.sessionGate')}</span>
            </Space>
          );
        }

        return (
          <Space direction="vertical" size={0}>
            <Tag color="purple" style={{ marginInlineEnd: 0 }}>{t('eventForm.gates.mainGate')}</Tag>
            <span style={{ fontSize: '11px', color: '#999' }}>{displayZoneName(zoneName || getDefaultMainZoneName())}</span>
          </Space>
        );
      }
    },
    {
      title: t('eventForm.gates.columns.location'),
      dataIndex: 'location',
      key: 'location',
      width: 150,
      render: (text) => text || <span style={{ color: '#999' }}>{t('eventForm.gates.notSpecified')}</span>
    },
    {
      title: t('eventForm.gates.columns.capacity'),
      dataIndex: 'capacity',
      key: 'capacity',
      width: 100,
      render: (capacity) => capacity || <span style={{ color: '#999' }}>{t('eventForm.gates.unlimited')}</span>
    },
    {
      title: t('eventForm.gates.columns.actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title={t('eventForm.gates.editGate')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={t('eventForm.gates.deleteGate')}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.gateId)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const handleEdit = (gate) => {
    setEditingGate(gate);
    const isMainGate = getGateScope(gate) === 'main';
    const level = isMainGate ? 'main' : 'session';
    setGateLevel(level);
    form.setFieldsValue({
      gateName: gate.gateName,
      gateType: gate.gateType,
      gateLevel: level,
      zoneName: !isMainGate ? gate.zoneName : undefined,
      capacity: gate.capacity,
      isActive: gate.isActive,
      description: gate.description,
      location: gate.location
    });
    setIsModalVisible(true);
  };

  const handleDelete = (gateId) => {
    Modal.confirm({
      title: t('eventForm.gates.deleteGate'),
      content: t('eventForm.gates.deleteConfirm'),
      okText: t('eventForm.gates.delete'),
      cancelText: t('common.cancel', 'Cancel'),
      okType: 'danger',
      onOk: () => {
        const updatedGates = gates.filter(g => g.gateId !== gateId);
        onChange(updatedGates);
        message.success(t('eventForm.gates.deleted'));
      }
    });
  };

  const handleAdd = () => {
    setEditingGate(null);
    setGateLevel('main');
    form.resetFields();
    form.setFieldsValue({
      gateType: 'entry',
      gateLevel: 'main',
      isActive: true
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const nextGateId = editingGate?.gateId || `gate-${Date.now()}`;
      const mainZoneId = getEventMainZoneId();
      const mainZoneName = getDefaultMainZoneName();

      let zoneData = {};
      if (values.gateLevel === 'main') {
        zoneData = {
          gateScope: 'main',
          zoneId: mainZoneId,
          zoneName: editingGate?.zoneName || mainZoneName,
          parentZoneId: null
        };
      } else {
        zoneData = {
          gateScope: 'session',
          zoneId: editingGate?.zoneId || buildSessionZoneId(nextGateId),
          zoneName: values.zoneName,
          parentZoneId: mainZoneId
        };
        values.gateType = 'both';
      }

      const { gateLevel: _, ...gateValues } = values;

      if (editingGate) {
        const updatedGates = gates.map(g =>
          g.gateId === editingGate.gateId
            ? { ...editingGate, ...gateValues, ...zoneData }
            : g
        );
        onChange(updatedGates);
        message.success(t('eventForm.gates.updated'));
      } else {
        const newGate = {
          gateId: nextGateId,
          ...gateValues,
          ...zoneData,
          createdAt: new Date().toISOString()
        };
        onChange([...gates, newGate]);
        message.success(t('eventForm.gates.added'));
      }

      setIsModalVisible(false);
      form.resetFields();
      setGateLevel('main');
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingGate(null);
    setGateLevel('main');
  };

  const g = (key) => t(`eventForm.gates.${key}`);

  return (
    <div dir={isRtl ? 'rtl' : 'ltr'}>
      <Card
        // With no gates yet the panel header above already says what this is, so skip the repeated title.
        title={
          gates && gates.length > 0 ? (
            <Space>
              <LoginOutlined />
              {g('title')}
            </Space>
          ) : null
        }
        bordered={!!(gates && gates.length > 0)}
        extra={
          gates && gates.length > 0 ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {g('addGate')}
            </Button>
          ) : null
        }
        styles={{
          header: { flexWrap: 'wrap', gap: 8, paddingBlock: 8, height: 'auto' },
          body: { padding: isMobile ? 8 : 16 }
        }}
      >
        {gates && gates.length > 0 ? (
          isMobile ? (
            <EntityCardList
              data={gates.map((gate) => ({ ...gate, id: gate.gateId }))}
              avatar={(gate) => (
                <span style={{ fontSize: 22, flexShrink: 0 }}>{gateTypeIcons[gate.gateType]}</span>
              )}
              title={(gate) => gate.gateName}
              subtitle={(gate) =>
                getGateScope(gate) === 'session'
                  ? gate.zoneName
                  : displayZoneName(gate.zoneName || getDefaultMainZoneName())
              }
              tags={(gate) => (
                <>
                  <Tag color={gateTypeColors[gate.gateType]}>
                    {t(`eventForm.gates.types.${gate.gateType}`, gate.gateType)}
                  </Tag>
                  <Tag color={getGateScope(gate) === 'session' ? 'blue' : 'purple'}>
                    {getGateScope(gate) === 'session' ? g('sessionGate') : g('mainGate')}
                  </Tag>
                  {!gate.isActive && <Tag color="red">{g('inactive')}</Tag>}
                </>
              )}
              details={(gate) => (
                <>
                  <span>
                    <EnvironmentOutlined /> {gate.location || g('notSpecified')}
                  </span>
                  <span>
                    {g('columns.capacity')}: {gate.capacity || g('unlimited')}
                  </span>
                </>
              )}
              menuItems={(gate) => [
                { key: 'edit', icon: <EditOutlined />, label: g('editGate'), onClick: () => handleEdit(gates.find((x) => x.gateId === gate.id)) },
                { key: 'delete', icon: <DeleteOutlined />, label: g('deleteGate'), danger: true, onClick: () => handleDelete(gate.id) },
              ]}
              onTap={(gate) => handleEdit(gates.find((x) => x.gateId === gate.id))}
              emptyText={g('noGates')}
            />
          ) : (
            <Table
              dataSource={gates}
              columns={columns}
              rowKey="gateId"
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
            />
          )
        ) : (
          <Empty
            description={g('noGates')}
            image={null}
            style={{ padding: '4px 0 8px', margin: 0 }}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              {g('addFirstGate')}
            </Button>
          </Empty>
        )}

        <Collapse
          ghost
          size="small"
          style={{ marginTop: 16, background: '#fafafa', borderRadius: 8 }}
          defaultActiveKey={[]}
          items={[
            {
              key: 'guide',
              label: <strong>{g('guide.title').replace(/[:：]\s*$/, '')}</strong>,
              children: (
                <div style={{ color: '#595959', fontSize: 13, lineHeight: 1.6 }}>
                  <div style={{ marginBottom: 12 }}>{g('guide.intro')}</div>
                  <GuideRow tag={<Tag color="purple" style={{ margin: 0 }}>{g('mainGate')}</Tag>}>
                    {g('guide.mainDesc')}
                  </GuideRow>
                  <GuideRow tag={<Tag color="blue" style={{ margin: 0 }}>{g('sessionGate')}</Tag>}>
                    {g('guide.sessionDesc')}
                  </GuideRow>

                  <div style={{ fontWeight: 600, margin: '16px 0 10px' }}>{g('guide.typesTitle')}</div>
                  <GuideRow tag={<Tag color="green" style={{ margin: 0 }}>{g('types.entry')}</Tag>}>
                    {g('guide.entryDesc')}
                  </GuideRow>
                  <GuideRow tag={<Tag color="orange" style={{ margin: 0 }}>{g('types.exit')}</Tag>}>
                    {g('guide.exitDesc')}
                  </GuideRow>
                  <GuideRow tag={<Tag color="blue" style={{ margin: 0 }}>{g('types.both')}</Tag>}>
                    {g('guide.bothDesc')}
                  </GuideRow>

                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0', fontSize: 12, color: '#8c8c8c' }}>
                    {g('guide.note')}
                  </div>
                </div>
              )
            }
          ]}
        />
      </Card>

      <Modal
        title={editingGate ? g('editGate') : g('addGate')}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        width="min(600px, calc(100vw - 32px))"
        okText={editingGate ? g('update') : g('add')}
        cancelText={t('common.cancel', 'Cancel')}
      >
        <div dir={isRtl ? 'rtl' : 'ltr'}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            gateType: 'entry',
            gateLevel: 'main',
            isActive: true
          }}
        >
          <Form.Item
            name="gateName"
            label={g('form.gateName')}
            rules={[
              { required: true, message: g('form.gateNameRequired') },
              { max: 50, message: g('form.gateNameMax') }
            ]}
          >
            <Input size="large" placeholder={g('form.gateNamePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="gateType"
            label={g('form.gateType')}
            rules={[{ required: true, message: g('form.gateTypeRequired') }]}
            extra={gateLevel === 'session' ? <div style={{ paddingTop: 4 }}>{g('form.gateTypeSessionHint')}</div> : undefined}
          >
            <Select size="large" disabled={gateLevel === 'session'}>
              <Option value="entry">
                <Space>
                  <LoginOutlined style={{ color: '#52c41a' }} />
                  {g('typeOptions.entry')}
                </Space>
              </Option>
              <Option value="exit">
                <Space>
                  <ExportOutlined style={{ color: '#fa8c16' }} />
                  {g('typeOptions.exit')}
                </Space>
              </Option>
              <Option value="both">
                <Space>
                  <SwapOutlined style={{ color: '#1890ff' }} />
                  {g('typeOptions.both')}
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="gateLevel"
            label={g('form.gateLevel')}
            rules={[{ required: true, message: g('form.gateLevelRequired') }]}
            extra={<div style={{ paddingTop: 4 }}>{g('form.gateLevelHint')}</div>}
          >
            <Select size="large" onChange={(value) => {
              setGateLevel(value);
              if (value === 'session') {
                form.setFieldsValue({ gateType: 'both' });
              }
            }}>
              <Option value="main">
                <Space>
                  <LoginOutlined style={{ color: '#9b59b6' }} />
                  {g('form.levelMain')}
                </Space>
              </Option>
              <Option value="session">
                <Space>
                  <SwapOutlined style={{ color: '#3498db' }} />
                  {g('form.levelSession')}
                </Space>
              </Option>
            </Select>
          </Form.Item>

          {gateLevel === 'session' && (
            <Form.Item
              name="zoneName"
              label={g('form.zoneName')}
              rules={[
                { required: true, message: g('form.zoneNameRequired') },
                { max: 50, message: g('form.zoneNameMax') }
              ]}
              extra={<div style={{ paddingTop: 4 }}>{g('form.zoneNameHint')}</div>}
            >
              <Input size="large" placeholder={g('form.zoneNamePlaceholder')} />
            </Form.Item>
          )}

          <Form.Item
            name="location"
            label={g('form.location')}
            rules={[{ max: 100, message: g('form.locationMax') }]}
          >
            <Input size="large" placeholder={g('form.locationPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="capacity"
            label={g('form.capacity')}
            extra={<div style={{ paddingTop: 4 }}>{g('form.capacityHint')}</div>}
          >
            <InputNumber
              size="large"
              min={0}
              placeholder={g('form.capacityPlaceholder')}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={g('form.description')}
            rules={[{ max: 200, message: g('form.descriptionMax') }]}
          >
            <TextArea
              rows={3}
              placeholder={g('form.descriptionPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="isActive"
            label={g('form.isActive')}
            valuePropName="checked"
          >
            <Switch checkedChildren={g('active')} unCheckedChildren={g('inactive')} />
          </Form.Item>
        </Form>
        </div>
      </Modal>
    </div>
  );
};

export default GateManagement;
